import { derived, type Readable } from 'svelte/store';
import {
  type CreateMutateFunction,
  createMutation,
  createQueries,
  createQuery,
} from '$lib/api/tanstack-query';

import { useNotify, useToast } from '@viamrobotics/prime-core';
import type * as APITypes from '@viamrobotics/web-client/app/v1/app';

import { assertExists } from '$lib/assert';
import { AuthStatus, useAuthContext } from '$lib/auth';
import { ResourceType, Role } from '$lib/permissions';
import type { QueryStateOptions } from '$lib/types/query';

import {
  useAPIClient,
  useFleetDashboardClient,
  useInternalAuthClient,
} from './client';
import { useQueryInvalidator } from './invalidate';
import { createQueryOptions } from './query-options';
import { locationKeys } from './locations/query-key';

export const createGetMachineAPIKeysQuery = (
  machineID: Readable<string | undefined>
) => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useAPIClient();

  const queryOptions = derived(
    [authState, machineID],
    ([$authState, $machineID]) =>
      createQueryOptions({
        queryKey: ['listRobotAPIKeys', $machineID],
        enabled:
          $authState.status === AuthStatus.LoggedIn && $machineID !== undefined,
        queryFn: async () => {
          const rpcOptions = await getRPCOptions();
          const { response } = await apiClient.getRobotAPIKeys(
            { robotId: $machineID ?? '' },
            rpcOptions
          );

          return response.apiKeys;
        },
      })
  );

  return createQuery(queryOptions);
};

export const createListKeysQuery = (orgID: Readable<string | undefined>) => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useAPIClient();
  const queryOptions = derived([authState, orgID], ([$authState, $orgID]) =>
    createQueryOptions({
      queryKey: ['listKeys', $orgID],
      enabled:
        $authState.status === AuthStatus.LoggedIn && $orgID !== undefined,
      queryFn: async () => {
        assertExists($orgID, 'Missing org ID');

        const rpcOptions = await getRPCOptions();
        const { response } = await apiClient.listKeys(
          { orgId: $orgID },
          rpcOptions
        );
        return response.apiKeys;
      },
    })
  );
  return createQuery(queryOptions);
};

export const createIsKeyDefaultQuery = (
  keyID: Readable<string | undefined>
) => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useInternalAuthClient();

  const queryOptions = derived([authState, keyID], ([$authState, $keyID]) =>
    createQueryOptions({
      queryKey: ['isKeyDefault', $keyID],
      enabled:
        $authState.status === AuthStatus.LoggedIn && $keyID !== undefined,
      queryFn: async () => {
        const rpcOptions = await getRPCOptions();
        const { response } = await apiClient.isKeyDefault(
          { id: $keyID ?? '' },
          rpcOptions
        );

        return response.isDefault;
      },
    })
  );

  return createQuery(queryOptions);
};

export const createDefaultStatusesStore = (
  apiKeys: APITypes.APIKeyWithAuthorizations[]
): Readable<Record<string, boolean>> => {
  const { authState, getRPCOptions } = useAuthContext();
  const apiClient = useInternalAuthClient();

  const keyIDs = apiKeys
    .map((k) => k.apiKey?.id)
    .filter((id): id is string => id !== undefined && id !== '');

  const queriesStore = createQueries({
    queries: derived(authState, ($authState) =>
      keyIDs.map((keyID) => ({
        queryKey: ['isKeyDefault', keyID],
        enabled: $authState.status === AuthStatus.LoggedIn,
        queryFn: async () => {
          const rpcOptions = await getRPCOptions();
          const { response } = await apiClient.isKeyDefault(
            { id: keyID },
            rpcOptions
          );
          return response.isDefault;
        },
      }))
    ),
  });

  return derived(queriesStore, ($results) => {
    const out: Record<string, boolean> = {};
    for (const [i, r] of $results.entries()) {
      const id = keyIDs[i];
      if (id && r.data !== undefined) {
        out[id] = r.data;
      }
    }
    return out;
  });
};

export const createListLocationKeysQuery = (locID: string | undefined) => {
  const { authState, getRPCOptions } = useAuthContext();
  const fleetDashboardClient = useFleetDashboardClient();
  const queryOptions = derived([authState], ([$authState]) =>
    createQueryOptions({
      queryKey: locationKeys.listLocKeys(locID),
      enabled: $authState.status === AuthStatus.LoggedIn && locID !== undefined,
      queryFn: async () => {
        assertExists(locID, 'Missing loc ID');

        const rpcOptions = await getRPCOptions();
        const { response } = await fleetDashboardClient.listLocationKeys(
          { locationId: locID },
          rpcOptions
        );
        return response.apiKeys;
      },
    })
  );
  return createQuery(queryOptions);
};

export interface CreateKeyParams {
  orgID: string;
  name: string;
  authorizationID: Role;
  resourceID: string;
  resourceType: ResourceType;
  locationID?: string;
}

export type CreateKey = CreateMutateFunction<
  APITypes.CreateKeyResponse,
  Error,
  CreateKeyParams
>;

export type CreateKeyWithNotify = (
  params: CreateKeyParams,
  options: QueryStateOptions
) => void;

export const useCreateKeyWithNotify = (): Readable<CreateKeyWithNotify> => {
  const notify = useNotify();
  const toast = useToast();
  const baseCreateKey = useCreateKey();

  return derived(
    baseCreateKey,
    ($baseCreateKey) =>
      (request: CreateKeyParams, options: QueryStateOptions) => {
        $baseCreateKey(request, {
          onSuccess: () =>
            options.successMessage &&
            toast({ message: options.successMessage, variant: 'success' }),
          onError: () =>
            options.errorMessage && notify.danger(options.errorMessage),
        });
      }
  );
};

export const useCreateKey = (): Readable<CreateKey> => {
  const mutation = createCreateKeyMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

export const createCreateKeyMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (params: CreateKeyParams) => {
      const authorizations = [
        {
          authorizationType: 'role',
          authorizationId: params.authorizationID,
          resourceType: params.resourceType,
          resourceId: params.resourceID,
          identityId: '',
          organizationId: params.orgID,
          identityType: 'api-key',
        },
      ];
      const request = { authorizations, name: params.name };
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.createKey(request, rpcOptions);

      return response;
    },
    onSuccess: async (_data, params) => {
      const keys = [
        ['listKeys', params.orgID],
        locationKeys.listLocKeys(params.locationID),
      ];

      if (params.resourceType === ResourceType.ROBOT) {
        keys.push(['listRobotAPIKeys', params.resourceID]);
      }

      await invalidateQueries(keys);
    },
  });
};

export interface DeleteKeyParams {
  keyID: string;
  orgID?: string;
  robotID?: string;
  locationID?: string;
}

export type DeleteKey = CreateMutateFunction<
  APITypes.DeleteKeyResponse,
  Error,
  DeleteKeyParams
>;

export type DeleteKeyWithNotify = (
  params: DeleteKeyParams,
  options: QueryStateOptions
) => void;

export const useDeleteKeyWithNotify = (): Readable<DeleteKeyWithNotify> => {
  const notify = useNotify();
  const toast = useToast();
  const baseDeleteKey = useDeleteKey();

  return derived(
    baseDeleteKey,
    ($baseDeleteKey) =>
      (request: DeleteKeyParams, options: QueryStateOptions) => {
        $baseDeleteKey(request, {
          onSuccess: () =>
            options.successMessage &&
            toast({ message: options.successMessage, variant: 'success' }),
          onError: () =>
            options.errorMessage && notify.danger(options.errorMessage),
        });
      }
  );
};

export const useDeleteKey = (): Readable<DeleteKey> => {
  const mutation = createDeleteKeyMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

export const createDeleteKeyMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (params: DeleteKeyParams) => {
      const request: APITypes.DeleteKeyRequest = { id: params.keyID };
      const rpcOptions = await getRPCOptions();
      const { response } = await apiClient.deleteKey(request, rpcOptions);

      return response;
    },
    onSuccess: async (_data, params) => {
      await invalidateQueries([
        ['listRobotAPIKeys', params.robotID],
        ['listKeys', params.orgID],
        locationKeys.listLocKeys(params.locationID),
      ]);
    },
  });
};

export interface DuplicateKeyParams {
  keyID: string;
  orgID: string;
}

export type DuplicateKeyWithNotify = (
  params: DuplicateKeyParams,
  options: QueryStateOptions
) => void;

export type DuplicateKey = CreateMutateFunction<
  APITypes.CreateKeyFromExistingKeyAuthorizationsResponse,
  Error,
  DuplicateKeyParams
>;

export const useDuplicateKeyWithNotify =
  (): Readable<DuplicateKeyWithNotify> => {
    const notify = useNotify();
    const toast = useToast();
    const baseDuplicateKey = useDuplicateKey();

    return derived(
      baseDuplicateKey,
      ($baseDuplicateKey) =>
        (request: DuplicateKeyParams, options: QueryStateOptions) => {
          $baseDuplicateKey(request, {
            onSuccess: () =>
              options.successMessage &&
              toast({ message: options.successMessage, variant: 'success' }),
            onError: () =>
              options.errorMessage && notify.danger(options.errorMessage),
          });
        }
    );
  };

export const useDuplicateKey = (): Readable<DuplicateKey> => {
  const mutation = createDuplicateKeyMutation();

  return derived([mutation], ([$mutation]) => $mutation.mutate);
};

export const createDuplicateKeyMutation = () => {
  const apiClient = useAPIClient();
  const { getRPCOptions } = useAuthContext();
  const { invalidateQueries } = useQueryInvalidator();

  return createMutation({
    mutationFn: async (params: DuplicateKeyParams) => {
      const request: APITypes.CreateKeyFromExistingKeyAuthorizationsRequest = {
        id: params.keyID,
      };
      const rpcOptions = await getRPCOptions();
      const { response } =
        await apiClient.createKeyFromExistingKeyAuthorizations(
          request,
          rpcOptions
        );

      return response;
    },
    onSuccess: async (_data, params) => {
      await invalidateQueries([['listKeys', params.orgID]]);
    },
  });
};
